/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { RateSessionSchema, type RateSessionInput } from '@therapy/shared-validators';
import { useCallback, type SyntheticEvent, type JSX } from 'react';
import { type UseFormRegisterReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type Session } from '../../lib/api/sessions.js';
import { useRateSession } from '../../lib/queries/sessions.js';

const FIELD_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';
const SCORES = [1, 2, 3, 4, 5] as const;

const scoreOrUndefined = (v: string): number | undefined => (v === '' ? undefined : Number(v));
const textOrUndefined = (v: string): string | undefined => (v.trim() === '' ? undefined : v);

/** A 1–5 score select with an explicit "not rated" empty option. */
function ScoreField({
  id,
  label,
  registration,
}: {
  readonly id: string;
  readonly label: string;
  readonly registration: UseFormRegisterReturn;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      <select id={id} className={FIELD_CLASS} defaultValue="" {...registration}>
        <option value="">{t('therapist.rate.unrated')}</option>
        {SCORES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Post-session rating form (P2-10, BRD §4 M02 #4). Prefills any existing scores. */
export function RateSessionForm({
  session,
  onDone,
}: {
  readonly session: Session;
  readonly onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const rate = useRateSession();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<RateSessionInput>({
    resolver: zodResolver(RateSessionSchema),
    defaultValues: {
      ...(session.engagementRating !== null ? { engagement: session.engagementRating } : {}),
      ...(session.cooperationRating !== null ? { cooperation: session.cooperationRating } : {}),
      ...(session.ratingNote !== null ? { note: session.ratingNote } : {}),
    },
  });

  const submit = useCallback(
    async (values: RateSessionInput): Promise<void> => {
      await rate.mutateAsync({ id: session.id, input: values });
      onDone();
    },
    [rate, session.id, onDone],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <ScoreField
        id="rate-engagement"
        label={t('therapist.rate.engagement')}
        registration={register('engagement', { setValueAs: scoreOrUndefined })}
      />
      <ScoreField
        id="rate-cooperation"
        label={t('therapist.rate.cooperation')}
        registration={register('cooperation', { setValueAs: scoreOrUndefined })}
      />
      <div>
        <label htmlFor="rate-note" className={LABEL_CLASS}>
          {t('therapist.rate.note')}
        </label>
        <textarea
          id="rate-note"
          rows={3}
          className={FIELD_CLASS}
          {...register('note', { setValueAs: textOrUndefined })}
        />
      </div>
      {rate.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || rate.isPending}>
          {t('therapist.rate.submit')}
        </Button>
      </div>
    </form>
  );
}
